import React from 'react';
import { Gem, Sparkles, Wind, Crown, ShieldCheck, Waves, Feather, Star } from 'lucide-react';
import { motion } from 'framer-motion';
import Reveal from './Reveal';
import { ease } from '../lib/motion';
import imperialLuxeBanner from '../assets/imperial_luxe_banner.webp';
import silkDynastyBanner from '../assets/silk_dynasty_banner.webp';

const BANNERS = {
  'imperial-luxe': imperialLuxeBanner,
  'silk-dynasty': silkDynastyBanner,
};

const DIAGRAMS = {
  'imperial-luxe': {
    label: 'The Imperial Luxe Story',
    tagline: 'Premium human hair, engineered for silky-smooth, salon-perfect elegance.',
    steps: [
      { icon: Gem, title: 'Premium Human Hair', text: 'Hand-selected premium-quality strands, full and thick root to tip.' },
      { icon: Feather, title: 'Silky-Soft Texture', text: 'A refined, lightweight feel that glides and never weighs you down.' },
      { icon: Wind, title: 'Natural Movement', text: 'Fluid, effortless swing that mirrors your own hair in motion.' },
      { icon: Crown, title: 'Salon-Quality Finish', text: 'A polished, put-together look that reads luxury from every angle.' },
      { icon: ShieldCheck, title: 'Exceptional Durability', text: 'Built to hold its beauty install after install.' },
    ],
  },
  'silk-dynasty': {
    label: 'The Silk Dynasty Story',
    tagline: 'Minimally processed virgin hair for rich, full-bodied, bouncy volume.',
    steps: [
      { icon: Gem, title: 'Virgin Human Hair', text: 'Minimally processed to preserve the cuticle and raw integrity.' },
      { icon: Waves, title: 'Rich Fullness', text: 'Dense, voluminous body that fills out every style beautifully.' },
      { icon: Sparkles, title: 'Natural Luster', text: 'Keeps its own healthy shine and bounce wash after wash.' },
      { icon: Crown, title: 'Glamorous Volume', text: 'Made for dramatic curls and voluminous, red-carpet waves.' },
      { icon: ShieldCheck, title: 'Long-Lasting Wear', text: 'Tangle-resistant and durable for months of gorgeous wear.' },
    ],
  },
};

const COMPARE = [
  { attr: 'Hair Grade', il: 'Premium human hair', sd: 'Virgin, minimally processed' },
  { attr: 'Signature Feel', il: 'Silky-soft & sleek', sd: 'Rich, full-bodied bounce' },
  { attr: 'Luster', il: 'Polished, refined shine', sd: 'Natural raw luster' },
  { attr: 'Best For', il: 'Sleek straight & smooth waves', sd: 'Voluminous curls & glam waves' },
  { attr: 'Movement', il: 'Effortless & fluid', sd: 'Bouncy & voluminous' },
  { attr: 'Longevity', il: 'Exceptional durability', sd: 'Long-lasting, tangle-resistant' },
  { attr: 'Starting Price', il: '$120 / bundle', sd: '$99 / bundle' },
];

const CollectionInfo = ({ collection }) => {
  const d = DIAGRAMS[collection];
  if (!d) return null;
  const active = collection === 'imperial-luxe' ? 'il' : 'sd';

  return (
    <section className="bg-[#0b0b0b] py-20 sm:py-28 overflow-hidden" data-testid={`collection-info-${collection}`}>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
        {/* Collection banner */}
        <Reveal>
          <div className="rounded-xl overflow-hidden shadow-2xl ring-1 ring-[#c6a15b]/25" data-testid={`collection-banner-${collection}`}>
            <img src={BANNERS[collection]} alt={`${d.label} banner`} className="w-full h-auto block" />
          </div>
        </Reveal>

        {/* Comparison chart */}
        <Reveal delay={0.1}>
          <div className="mt-20 max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <p className="overline text-gold-gradient mb-2">Imperial Luxe vs Silk Dynasty</p>
              <h3 className="font-display text-2xl sm:text-4xl text-[#f7f3ec]">Which Queen Are You?</h3>
            </div>
            <div className="overflow-hidden border border-[#c6a15b]/25 rounded-lg" data-testid={`compare-chart-${collection}`}>
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-[#141311]">
                    <th className="p-4 overline text-[0.6rem] text-[#9c9587] font-normal">Feature</th>
                    <th className={`p-4 overline text-[0.62rem] font-normal ${active === 'il' ? 'text-[#e7d3a1] bg-[#c6a15b]/10' : 'text-[#cfc7b8]'}`}>Imperial Luxe</th>
                    <th className={`p-4 overline text-[0.62rem] font-normal ${active === 'sd' ? 'text-[#e7d3a1] bg-[#c6a15b]/10' : 'text-[#cfc7b8]'}`}>Silk Dynasty</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARE.map((row, i) => (
                    <motion.tr
                      key={row.attr}
                      initial={{ opacity: 0, x: -24 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true, amount: 0.6 }}
                      transition={{ duration: 0.4, delay: i * 0.08, ease: ease.entrance }}
                      className={i % 2 ? 'bg-[#0f0e0c]' : 'bg-[#121110]'}
                    >
                      <td className="p-4 font-body text-xs text-[#9c9587] border-t border-[#c6a15b]/10">{row.attr}</td>
                      <td className={`p-4 font-body text-sm border-t border-[#c6a15b]/10 ${active === 'il' ? 'text-[#f7f3ec] bg-[#c6a15b]/[0.06]' : 'text-[#cfc7b8]'}`}>{row.il}</td>
                      <td className={`p-4 font-body text-sm border-t border-[#c6a15b]/10 ${active === 'sd' ? 'text-[#f7f3ec] bg-[#c6a15b]/[0.06]' : 'text-[#cfc7b8]'}`}>{row.sd}</td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-center font-body text-xs text-[#6b6459] mt-4 flex items-center justify-center gap-1.5">
              <Star size={12} className="fill-[#c6a15b] text-[#c6a15b]" /> Highlighted column is the collection you're viewing.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default CollectionInfo;
